import Grid from '@react-css/grid';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import typography from 'storybookConfig/mixins/typography';

import useAlertQueue from 'hooks/useAlertQueue';
import { selectCompany, selectCompanyId } from 'store/selectors/me/company';
import { updateShopifySettings } from 'store/slices/company';
import { useAppDispatch } from 'store';
import Modal from 'storybookConfig/stories/cells/Modal';
import PrimaryButton from 'storybookConfig/stories/molecules/Button/PrimaryButton';
import SecondaryButton from 'storybookConfig/stories/molecules/Button/SecondaryButton';
import Checkbox from 'storybookConfig/stories/molecules/Checkbox';
import { getShopifyLocations, type ShopifyLocation } from 'utils/api/companies';
import Body from 'storybookConfig/stories/molecules/Body';

interface ShopifyLocationsModalProps {
  show: boolean;
  onHide: () => void;
}

const StyledLocationCard = styled.div<{ isSelected: boolean }>`
  border: 1px solid
    ${({ isSelected, theme }) => (isSelected ? theme.color.accent : theme.color.borderMedium)};
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  background-color: ${({ isSelected, theme }) =>
    isSelected ? theme.color.accentSubtle : 'transparent'};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.color.accent};
    background-color: ${({ theme }) => theme.color.accentSubtle};
  }
`;

const LocationName = styled.div`
  font-weight: 700;
  margin-bottom: 8px;
  font-size: 14px;
`;

const LocationAddress = styled.div`
  ${typography('Body/Caption')};
  color: ${({ theme }) => theme.color.bodyTextSecondary};
`;

const LocationsGrid = styled(Grid)`
  margin-top: 16px;
  gap: 16px;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
`;

const ShopifyLocationsModal = ({ show, onHide }: ShopifyLocationsModalProps) => {
  const dispatch = useAppDispatch();
  const { addSuccessAlert, addErrorAlert } = useAlertQueue();
  const company = useSelector(selectCompany);
  const companyId = useSelector(selectCompanyId);

  const [locations, setLocations] = useState<ShopifyLocation[]>([]);
  const [locationIds, setLocationIds] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const currentLocationIds = useMemo<number[]>(
    () => company?.shopify?.locationIds || [],
    [company]
  );

  useEffect(() => {
    if (show && companyId) {
      setIsLoading(true);
      getShopifyLocations(companyId)
        .then((response) => {
          setLocations(response.data);
          setLocationIds(currentLocationIds);
        })
        .catch((error) => {
          addErrorAlert('Failed to load locations', error?.message || 'Unknown error');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [show, companyId, currentLocationIds, addErrorAlert]);

  const handleLocationToggle = (locationId: number) => {
    setLocationIds((prev) => {
      if (prev.includes(locationId)) {
        return prev.filter((id) => id !== locationId);
      }
      return [...prev, locationId];
    });
  };

  const handleSave = () => {
    if (!companyId) return;

    setIsSaving(true);

    dispatch(
      updateShopifySettings({
        locationIds,
      })
    )
      .then((action: { error?: { message: string } }) => {
        if (action.error) {
          addErrorAlert('Failed to save locations', action.error.message);
          return;
        }
        addSuccessAlert('Success', 'Shopify locations updated successfully');
        onHide();
      })
      .catch((error: { message?: string }) => {
        addErrorAlert('Failed to save locations', error?.message || 'Unknown error');
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const formatAddress = (address: ShopifyLocation['address']) => {
    const parts = [
      address.addressOne,
      address.addressTwo,
      [address.city, address.state].filter(Boolean).join(', '),
      address.zip,
      address.country,
    ].filter(Boolean);

    return parts.join('\n');
  };

  return (
    <Modal.Root show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Shopify Locations</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Body variant="Body/Body Small" color="bodyTextCaption">
          Select the locations you want to use for inventory filtering. Only locations that can
          fulfill online orders are shown.
        </Body>

        {isLoading && (
          <div style={{ textAlign: 'center', padding: '32px' }}>Loading locations...</div>
        )}
        {!isLoading && locations.length === 0 && (
          <div style={{ textAlign: 'center', padding: '32px' }}>No locations found</div>
        )}
        {!isLoading && locations.length > 0 && (
          <LocationsGrid>
            {locations.map((location) => {
              const isSelected = locationIds.includes(location.id);
              return (
                <StyledLocationCard
                  key={location.id}
                  isSelected={isSelected}
                  onClick={() => handleLocationToggle(location.id)}
                >
                  <Checkbox
                    checked={isSelected}
                    onChange={() => handleLocationToggle(location.id)}
                    onClick={(e) => e.stopPropagation()}
                    name={`location-${location.id}`}
                  />
                  <LocationName>{location.name}</LocationName>
                  <LocationAddress>{formatAddress(location.address)}</LocationAddress>
                </StyledLocationCard>
              );
            })}
          </LocationsGrid>
        )}
      </Modal.Body>
      <Modal.Footer>
        <SecondaryButton size="small" onClick={onHide} disabled={isSaving}>
          Cancel
        </SecondaryButton>
        <PrimaryButton size="small" onClick={handleSave} disabled={isLoading || isSaving}>
          {isSaving ? 'Saving...' : 'Save'}
        </PrimaryButton>
      </Modal.Footer>
    </Modal.Root>
  );
};

export default ShopifyLocationsModal;
